// components/features/subscriptions/SubscriptionRow.tsx

import { Subscription } from "../../../types/features/Subscription";
import TableRow from "../../ui/TableRow";

interface SubscriptionRowProps {
  subscription: Subscription;
  onAction: (subscription: Subscription) => void;
  onSelect?: (subscription: Subscription, isSelected: boolean) => void;
  isSelected?: boolean;
  selectable?: boolean;
}

export default function SubscriptionRow({
  subscription,
  onAction,
  onSelect,
  isSelected = false,
  selectable = false
}: SubscriptionRowProps) {
  // Couleur du statut
  const getStatusBadge = (status: string) => {
    const statuses = {
      active: "bg-green-100 text-green-800",
      inactive: "bg-gray-100 text-gray-800",
      suspended: "bg-red-100 text-red-800",
      trial: "bg-blue-100 text-blue-800",
      expired: "bg-yellow-100 text-yellow-800"
    };
    return statuses[status as keyof typeof statuses] || "bg-gray-100 text-gray-800";
  };

  // Label du plan
  const getPlanLabel = (plan: string) => {
    const plans = {
      starter: "Démarrage",
      professional: "Professionnel",
      enterprise: "Entreprise"
    };
    return plans[plan as keyof typeof plans] || plan;
  };

  // Couleur du plan
  const getPlanColor = (plan: string) => {
    const colors = {
      starter: "bg-gray-100 text-gray-700",
      professional: "bg-blue-100 text-blue-700",
      enterprise: "bg-purple-100 text-purple-700"
    };
    return colors[plan as keyof typeof colors] || "bg-gray-100 text-gray-700";
  };

  const formatPrice = (price: number, currency: string) => {
    const symbols = {
      EUR: "€",
      USD: "$",
      XOF: "CFA"
    };
    return `${symbols[currency as keyof typeof symbols] || ""} ${price.toLocaleString()}`;
  };

  const isExpired = new Date(subscription.end_date) < new Date();

  return (
    <TableRow
      item={subscription}
      onAction={onAction}
      onSelect={onSelect}
      isSelected={isSelected}
      selectable={selectable}
      actionable={true}
    >
      {/* Colonne Entreprise */}
      <td className="py-3 px-4">
        <div className="flex items-center space-x-3">
          <div className="h-9 w-9 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-semibold text-xs shrink-0 border border-indigo-200">
            {subscription.entreprise_name.charAt(0).toUpperCase()}
          </div>
          <div>
            <span className="text-sm font-medium text-gray-900 block truncate max-w-[150px]">
              {subscription.entreprise_name}
            </span>
            <span className="text-xs text-gray-500">
              ID: {subscription.entreprise_id.substring(0, 8)}
            </span>
          </div>
        </div>
      </td>

      {/* Colonne Plan */}
      <td className="py-3 px-4 whitespace-nowrap">
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getPlanColor(subscription.plan)}`}>
          {getPlanLabel(subscription.plan)}
        </span>
      </td>

      {/* Colonne Statut */}
      <td className="hidden sm:table-cell py-3 px-4 whitespace-nowrap">
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadge(subscription.status)}`}>
          {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
        </span>
        {isExpired && subscription.status === "active" && (
          <span className="ml-2 text-xs text-red-500">⚠️ Expiré</span>
        )}
      </td>

      {/* Colonne Sièges */}
      <td className="hidden md:table-cell py-3 px-4 whitespace-nowrap">
        <div className="flex items-center">
          <span className="text-sm text-gray-700">
            {subscription.seats_used} / {subscription.seats}
          </span>
          <div className="ml-2 w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-indigo-600 rounded-full"
              style={{ width: `${(subscription.seats_used / subscription.seats) * 100}%` }}
            />
          </div>
        </div>
      </td>

      {/* Colonne Prix */}
      <td className="hidden lg:table-cell py-3 px-4 whitespace-nowrap">
        <span className="text-sm font-medium text-gray-900">
          {formatPrice(subscription.price, subscription.currency)}
        </span>
        <span className="text-xs text-gray-500 block">
          / mois
        </span>
      </td>

      {/* Colonne Date d'expiration */}
      <td className="hidden xl:table-cell py-3 px-4 whitespace-nowrap">
        <span className={`text-xs ${isExpired ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
          {new Date(subscription.end_date).toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
          })}
        </span>
        {subscription.auto_renew && (
          <span className="text-xs text-green-500 block">🔄 Renouvellement auto</span>
        )}
      </td>
    </TableRow>
  );
}